import TalkToCarrotTilePage from "#/components/talk-to-carrot/TalkToCarrotTilePage"
import React, { ReactElement } from "react"
import {
  fieldIsValid,
  getNextTileAction,
  getNextActionType,
  getPathForTile,
  isPhoneSupportNumber,
  sortByJourneySpecificOrder,
  TileNextActionType
} from "../utils/mappingUtils"
import { GuidanceElement } from "./GuidanceElement"
import { TileElement } from "./TileElement"
import { Redirect } from "react-router"
import { Paths } from "#/utils/Paths"
import { toCondition } from "../utils/conditionUtils"
import { useGetPhoneSupportNumberForUser } from "#/components/dynamic-content/hooks/useQueryPhoneSupport"
import { Journey } from "../utils/journeyOrderMapping"
import { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import { TypeTilePageSkeleton } from "#/types/contentful"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { useParentContentfulTileEntry } from "#/lib/contentful/utils/useContentfulTiles"
import { useDoesUserSatisfyConditions } from "#/services/contentful/conditional-gate"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"

type TTCTilePageElementProps = {
  page: EntryWithoutUnresolvableLinks<TypeTilePageSkeleton>
  baseUrl: string
}

const TTCTilePageElement = ({ page, baseUrl }: TTCTilePageElementProps): ReactElement<typeof TalkToCarrotTilePage> => {
  const { doesUserSatisfyConditions, isLoading: isDoesUserSatisfyConditionsLoading } = useDoesUserSatisfyConditions()

  const shouldFindParentTile = page.fields.tiles.some((tile) => {
    const nextAction = getNextTileAction(tile)
    return getNextActionType(nextAction) === TileNextActionType.CALENDLY_LINK
  })
  const { data: parentTiles } = useParentContentfulTileEntry(page.sys.id, shouldFindParentTile)
  const filteredTileEntry =
    shouldFindParentTile && !isDoesUserSatisfyConditionsLoading
      ? parentTiles?.filter(
          ({ fields: { conditions } }) => !conditions || doesUserSatisfyConditions(conditions.map(toCondition))
        )
      : null
  if (filteredTileEntry && filteredTileEntry.length > 1) {
    reportErrorMessage(
      `Contentful inconsistency: Multiple eligible parent tiles found for the Tile Page with entry id of ${page.sys.id}. Expected 1 or none, but found ${filteredTileEntry.length}.`
    )
  }
  const tileEntryId = filteredTileEntry?.[0]?.sys?.id || null

  const {
    fields: { guidance, tiles }
  } = page
  const { data: phoneSupportNumber, isError, isInitialLoading } = useGetPhoneSupportNumberForUser()
  const memberJourneyQuery = useGetMemberJourneyData()
  const journey = memberJourneyQuery.data?.currentMemberJourney.preferSubmittedRouting.journey

  const hasPhoneSupportNumber = tiles.some((tile) => isPhoneSupportNumber(getNextTileAction(tile)))

  if (
    isDoesUserSatisfyConditionsLoading ||
    memberJourneyQuery.isLoading ||
    (hasPhoneSupportNumber && isInitialLoading)
  ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }
  if (isError) {
    return <Redirect to="/talk-to-carrot/error" />
  }

  const conditionalAvailableTiles = tiles.filter(
    ({ fields: { conditions } }) => !conditions || doesUserSatisfyConditions(conditions.map(toCondition))
  )
  const availableTiles = conditionalAvailableTiles.filter((tile) =>
    isPhoneSupportNumber(getNextTileAction(tile)) ? Boolean(phoneSupportNumber.e164FormatPhoneNumber) : true
  )

  if (availableTiles.length === 0) {
    return <Redirect to={Paths.TALK_TO_CARROT} />
  }
  return (
    <>
      {availableTiles && fieldIsValid(guidance) ? (
        <TalkToCarrotTilePage
          guidance={<GuidanceElement guidance={guidance} />}
          tiles={availableTiles
            .filter(fieldIsValid)
            .sort((a, b) => {
              return sortByJourneySpecificOrder((journey as Journey) ?? "DEFAULT", a, b)
            })
            .map((tile) => (
              <TileElement
                tile={tile}
                baseUrl={baseUrl}
                key={`${getPathForTile({ tile, baseUrl })}_tile`}
                doesUserSatisfyConditions={doesUserSatisfyConditions}
                memberJourney={journey}
                tileEntryId={tileEntryId}
              />
            ))}
        />
      ) : null}
    </>
  )
}

export { TTCTilePageElement }
